import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SimilarJobs from '../components/SimilarJobs';
import SubscriptionSection from '../components/SubscriptionSection';
import { sendJobApplication, openEmailClient, testEmailJS } from '../services/emailService';
import {
  getDepartmentDisplayName,
  getJobTypeDisplayName,
  getExperienceLevelDisplayName,
  formatSalary,
  formatDate,
  isDeadlineNear,
  isDeadlinePassed,
  formatTextWithLineBreaks,
  formatRequirements,
  formatBenefits
} from '../utils/jobUtils';
import '../styles/JobDetail.css';

const JobDetail = () => {
  const { identifier } = useParams(); // Could be ID or slug
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const API_BASE_URL = "https://phucansolutions.vn" ;

  useEffect(() => {
    fetchJobDetail();
    checkIfJobSaved();
  }, [identifier]);

  const fetchJobDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`🔄 Fetching job detail for: ${identifier}`);
      
      const response = await fetch(`${API_BASE_URL}/api/jobs/${identifier}`);

      if (!response.ok) {
        if (response.status === 404) {
          setError('Không tìm thấy tin tuyển dụng này');
        } else {
          throw new Error(`API error: ${response.status}`);
        }
        return;
      }

      const data = await response.json();

      if (data.success && data.data) {
        setJob(data.data);
      } else {
        setError('Không thể tải thông tin tin tuyển dụng');
      }
      
    } catch (err) {
      console.error('❌ Error fetching job detail:', err);
      setError('Có lỗi xảy ra khi tải trang');
    } finally {
      setLoading(false);
    }
  };

  // Check if job is already saved
  const checkIfJobSaved = () => {
    const savedJobs = JSON.parse(localStorage.getItem('savedJobs') || '[]');
    setIsSaved(savedJobs.includes(identifier));
  };

  // Handle save/unsave job
  const handleSaveJob = () => {
    const savedJobs = JSON.parse(localStorage.getItem('savedJobs') || '[]');
    
    if (isSaved) {
      // Remove from saved
      const updatedJobs = savedJobs.filter(id => id !== identifier);
      localStorage.setItem('savedJobs', JSON.stringify(updatedJobs));
      setIsSaved(false);
      
      // Show notification
      alert('Đã bỏ lưu việc làm');
    } else {
      // Add to saved
      savedJobs.push(identifier);
      localStorage.setItem('savedJobs', JSON.stringify(savedJobs));
      setIsSaved(true);
      
      // Show notification
      alert('Đã lưu việc làm vào danh sách yêu thích');
    }
  };

  // Handle share job
  const handleShareJob = () => {
    setShowShareMenu(!showShareMenu);
  };

  // Share via different methods
  const shareVia = (method) => {
    const jobUrl = window.location.href;
    const jobTitle = job?.title || 'Cơ hội việc làm tại PhucAn Solutions';
    const shareText = `${jobTitle} - ${jobUrl}`;

    switch (method) {
      case 'copy':
        navigator.clipboard.writeText(jobUrl).then(() => {
          alert('Đã sao chép link việc làm');
          setShowShareMenu(false);
        });
        break;
      
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(jobUrl)}`, '_blank');
        setShowShareMenu(false);
        break;
      
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}`, '_blank');
        setShowShareMenu(false);
        break;
      
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(jobTitle)}&body=${encodeURIComponent(shareText)}`;
        setShowShareMenu(false);
        break;
      
      default:
        if (navigator.share) {
          navigator.share({
            title: jobTitle,
            text: `Cơ hội việc làm tuyệt vời tại PhucAn Solutions`,
            url: jobUrl,
          });
        } else {
          // Fallback to copy
          navigator.clipboard.writeText(jobUrl).then(() => {
            alert('Đã sao chép link việc làm');
          });
        }
        setShowShareMenu(false);
    }
  };

  // Debug function
  const handleDebugEmailJS = async () => {
    console.log('🔧 Debug: Testing EmailJS...');
    try {
      const result = await testEmailJS();
      console.log('Debug result:', result);
      alert(result.success ? '✅ EmailJS test successful!' : '❌ EmailJS test failed: ' + result.message);
    } catch (error) {
      console.error('Debug error:', error);
      alert('❌ Debug error: ' + error.message);
    }
  };

  // Get job image URL (matching News.jsx pattern)
  const getJobImageUrl = (job) => {
    if (!job) return '/images';
    
    const image = job.image;
    if (!image || image === '/images') {
      return '/images';
    }
    
    // If image starts with /uploads, add API base URL
    if (image.startsWith('/uploads/')) {
      return `${API_BASE_URL}${image}`;
    }
    
    return image;
  };

  if (loading) {
    return (
      <div className="job-detail-main">
        <div className="job-detail-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Đang tải thông tin việc làm...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-detail-main">
        <div className="job-detail-container">
          <div className="error-state">
            <div className="error-icon">❌</div>
            <h2>Oops! Có lỗi xảy ra</h2>
            <p>{error}</p>
            <div className="error-actions">
              <button onClick={() => navigate('/jobs')} className="back-to-jobs-btn">
                ← Quay lại danh sách việc làm
              </button>
              <button onClick={fetchJobDetail} className="retry-btn">
                🔄 Thử lại
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-detail-main">
        <div className="job-detail-container">
          <div className="not-found-state">
            <div className="not-found-icon">🔍</div>
            <h2>Không tìm thấy tin tuyển dụng</h2>
            <p>Tin tuyển dụng này có thể đã được gỡ bỏ hoặc không còn tồn tại.</p>
            <button onClick={() => navigate('/jobs')} className="back-to-jobs-btn">
              ← Quay lại danh sách việc làm
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="job-detail-main">
      <div className="job-detail-container">
        <div className="job-detail-grid">
          {/* Main Content */}
          <div className="job-detail-main-content">
            {/* Job Header */}
            <div className="job-detail-header">
              {/* Job Image */}
              <div className="job-detail-image">
                <img 
                  src={getJobImageUrl(job)} 
                  alt={`Tuyển dụng ${job.title}`}
                  onError={(e) => {
                    e.target.src = '/images';
                  }}
                />
              </div>

              <div className="job-header-badges">
                <span className="department-badge">
                  🏢 {getDepartmentDisplayName(job.department)}
                </span>
                {job.remote_allowed && (
                  <span className="remote-badge">
                    🌐 Remote
                  </span>
                )}
              </div>
              
              <h1 className="job-detail-title">{job.title}</h1>
              
              <div className="job-header-meta">
                <div className="meta-item">
                  <i className="fas fa-building"></i>
                  <span>PhucAn Solutions</span>
                </div>
                <div className="meta-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>{job.location}</span>
                </div>
                <div className="meta-item">
                  <i className="fas fa-clock"></i>
                  <span>Đăng {formatDate(job.created_at)}</span>
                </div>
                <div className="meta-item">
                  <i className="fas fa-eye"></i>
                  <span>{job.views} lượt xem</span>
                </div>
              </div>
            </div>

            {/* Job Summary */}
            {job.summary && (
              <div className="job-section">
                <h2>📋 Tóm tắt công việc</h2>
                <div className="job-summary">
                  {formatTextWithLineBreaks(job.summary)}
                </div>
              </div>
            )}

            {/* Job Description */}
            {job.description && (
              <div className="job-section">
                <h2>📝 Mô tả công việc</h2>
                <div className="job-description">
                  {formatTextWithLineBreaks(job.description)}
                </div>
              </div>
            )}

            {/* Requirements */}
            {job.requirements && (
              <div className="job-section">
                <h2>✅ Yêu cầu ứng viên</h2>
                <ul className="job-requirements">
                  {formatRequirements(job.requirements).map((req, index) => (
                    <li key={index}>{req}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Benefits */}
            {job.benefits && (
              <div className="job-section">
                <h2>🎁 Quyền lợi & Phúc lợi</h2>
                <ul className="job-benefits">
                  {formatBenefits(job.benefits).map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Contact Information */}
            {(job.contact_email || job.contact_phone) && (
              <div className="job-section">
                <h2>📞 Thông tin liên hệ</h2>
                <div className="contact-info">
                  {job.contact_email && (
                    <div className="contact-item">
                      <i className="fas fa-envelope"></i>
                      <a href={`mailto:${job.contact_email}`}>{job.contact_email}</a>
                    </div>
                  )}
                  {job.contact_phone && (
                    <div className="contact-item">
                      <i className="fas fa-phone"></i>
                      <a href={`tel:${job.contact_phone}`}>{job.contact_phone}</a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="job-detail-sidebar">
            {/* Application Deadline Alert */}
            {job.application_deadline && (
              <div className={`deadline-alert ${isDeadlinePassed(job.application_deadline) ? 'expired' : isDeadlineNear(job.application_deadline) ? 'urgent' : ''}`}>
                <div className="deadline-icon">
                  {isDeadlinePassed(job.application_deadline) ? '⏰' : '📅'}
                </div>
                <div className="deadline-content">
                  <div className="deadline-label">Hạn nộp hồ sơ</div>
                  <div className="deadline-date">{formatDate(job.application_deadline)}</div>
                  {isDeadlinePassed(job.application_deadline) && (
                    <div className="deadline-status">Đã hết hạn</div>
                  )}
                  {isDeadlineNear(job.application_deadline) && !isDeadlinePassed(job.application_deadline) && (
                    <div className="deadline-status">Sắp hết hạn</div>
                  )}
                </div>
              </div>
            )}

            {/* Job Details Card */}
            <div className="job-details-card">
              <h3>💼 Thông tin chi tiết</h3>
              
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="detail-label">Loại hình:</span>
                  <span className="detail-value">{getJobTypeDisplayName(job.job_type)}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Kinh nghiệm:</span>
                  <span className="detail-value">{getExperienceLevelDisplayName(job.experience_level)}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Mức lương:</span>
                  <span className="detail-value salary">{formatSalary(job.min_salary, job.max_salary)}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Số lượng tuyển:</span>
                  <span className="detail-value">{job.positions_available} vị trí</span>
                </div>
              </div>
            </div>

            {/* Application Actions */}
            <div className="application-actions">
              {!isDeadlinePassed(job.application_deadline) ? (
                <>
                  <button 
                    className="apply-btn primary"
                    onClick={() => setShowApplicationForm(true)}
                  >
                    <i className="fas fa-paper-plane"></i>
                    Ứng tuyển ngay
                  </button>
                  <button className="save-job-btn secondary" onClick={handleSaveJob}>
                    <i className="fas fa-bookmark"></i>
                    {isSaved ? 'Bỏ lưu' : 'Lưu việc làm'}
                  </button>
                  <button className="share-job-btn secondary" onClick={handleShareJob}>
                    <i className="fas fa-share-alt"></i>
                    Chia sẻ
                  </button>
                  {/* Debug button - remove in production */}
                  {process.env.NODE_ENV === 'development' && (
                    <button className="debug-btn secondary" onClick={handleDebugEmailJS}>
                      <i className="fas fa-bug"></i>
                      Debug EmailJS
                    </button>
                  )}
                </>
              ) : (
                <div className="expired-notice">
                  <i className="fas fa-exclamation-triangle"></i>
                  <p>Tin tuyển dụng này đã hết hạn nộp hồ sơ</p>
                </div>
              )}
            </div>

            {/* Similar Jobs Component */}
            <SimilarJobs 
              currentJobId={job.id}
              department={job.department}
              jobType={job.job_type}
              location={job.location}
            />
          </div>
        </div>
      </div>

      {/* Application Form Modal */}
      {showApplicationForm && (
        <div className="application-modal">
          <div className="modal-overlay" onClick={() => setShowApplicationForm(false)}></div>
          <div className="modal-content">
            <div className="modal-header">
              <h3>📝 Ứng tuyển: {job.title}</h3>
              <button 
                className="modal-close"
                onClick={() => setShowApplicationForm(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <form className="application-form" onSubmit={async (e) => {
                e.preventDefault();
                console.log('🚀 Form submission started');
                setIsSubmitting(true);
                
                try {
                  // Get form data
                  const formData = new FormData(e.target);
                  const applicationData = {
                    fullName: formData.get('fullName'),
                    email: formData.get('email'),
                    phone: formData.get('phone'),
                    experience: formData.get('experience'),
                    coverLetter: formData.get('coverLetter'),
                  };
                  
                  console.log('📝 Form data collected:', applicationData);
                  
                  // Validate required fields
                  if (!applicationData.fullName || !applicationData.email || !applicationData.phone) {
                    alert('⚠️ Vui lòng điền đầy đủ thông tin bắt buộc (Họ tên, Email, Số điện thoại)');
                    console.log('❌ Validation failed - missing required fields');
                    return;
                  }
                  
                  // Validate email format
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(applicationData.email)) {
                    alert('⚠️ Vui lòng nhập địa chỉ email hợp lệ');
                    console.log('❌ Validation failed - invalid email format');
                    return;
                  }
                  
                  console.log('✅ Validation passed, attempting to send email...');
                  
                  // Try to send via EmailJS first
                  const emailResult = await sendJobApplication(applicationData, job);
                  
                  console.log('📧 Email result:', emailResult);
                  
                  if (emailResult.success) {
                    // Success - show success message
                    alert('✅ ' + emailResult.message + '\n\n📎 Lưu ý: Vui lòng gửi riêng CV/Resume qua email để hoàn tất hồ sơ ứng tuyển.');
                    setShowApplicationForm(false);
                    console.log('✅ Application submitted successfully via EmailJS');
                  } else {
                    // EmailJS failed - fallback to email client
                    console.log('⚠️ EmailJS failed, using fallback:', emailResult.message);
                    alert('⚠️ ' + emailResult.message + '\n\n🔄 Đang mở ứng dụng email để bạn có thể gửi trực tiếp...');
                    
                    // Small delay then open email client
                    setTimeout(() => {
                      openEmailClient(applicationData, job);
                    }, 1000);
                    
                    setShowApplicationForm(false);
                  }
                  
                } catch (error) {
                  console.error('💥 Application submission error:', error);
                  console.error('Error stack:', error.stack);
                  
                  alert('❌ Có lỗi xảy ra khi gửi ứng tuyển: ' + error.message + '\n\nĐang mở ứng dụng email để bạn có thể gửi trực tiếp...');
                  
                  // Fallback to email client
                  try {
                    const formData = new FormData(e.target);
                    const applicationData = {
                      fullName: formData.get('fullName'),
                      email: formData.get('email'),
                      phone: formData.get('phone'),
                      experience: formData.get('experience'),
                      coverLetter: formData.get('coverLetter'),
                    };
                    
                    setTimeout(() => {
                      openEmailClient(applicationData, job);
                    }, 1000);
                  } catch (fallbackError) {
                    console.error('💥 Fallback also failed:', fallbackError);
                  }
                  
                  setShowApplicationForm(false);
                } finally {
                  setIsSubmitting(false);
                  console.log('🏁 Form submission process completed');
                }
              }}>
                <div className="form-group">
                  <label htmlFor="fullName">Họ và tên *</label>
                  <input 
                    type="text" 
                    id="fullName" 
                    name="fullName" 
                    required 
                    placeholder="Nhập họ và tên đầy đủ"
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    required 
                    placeholder="example@email.com"
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Số điện thoại *</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    required 
                    placeholder="0123456789"
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="experience">Kinh nghiệm (năm)</label>
                  <select id="experience" name="experience" disabled={isSubmitting}>
                    <option value="">Chọn kinh nghiệm</option>
                    <option value="Mới tốt nghiệp">Mới tốt nghiệp</option>
                    <option value="1-2 năm">1-2 năm</option>
                    <option value="3-5 năm">3-5 năm</option>
                    <option value="5+ năm">5+ năm</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="coverLetter">Thư giới thiệu</label>
                  <textarea 
                    id="coverLetter" 
                    name="coverLetter" 
                    rows="4"
                    placeholder="Giới thiệu ngắn gọn về bản thân và lý do ứng tuyển..."
                    disabled={isSubmitting}
                  ></textarea>
                </div>
                
                <div className="form-notice">
                  <div className="notice-content">
                    <i className="fas fa-info-circle"></i>
                    <div>
                      <p><strong>Lưu ý về CV/Resume:</strong></p>
                      <p>Sau khi gửi form này, vui lòng gửi riêng CV/Resume của bạn đến email <strong>support@phucansolutions.vn</strong> để hoàn tất hồ sơ ứng tuyển.</p>
                    </div>
                  </div>
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={() => setShowApplicationForm(false)} 
                    className="cancel-btn"
                    disabled={isSubmitting}
                  >
                    Hủy
                  </button>
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Đang gửi...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i>
                        Gửi ứng tuyển
                      </>
                    )}
                  </button>
                </div>
              </form>
              
              <div className="alternative-contact">
                <p>Hoặc gửi trực tiếp qua email:</p>
                <a href={`mailto:support@phucansolutions.vn?subject=Ứng tuyển ${job.title}`} className="email-link">
                  support@phucansolutions.vn
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Menu */}
      {showShareMenu && (
        <div className="share-menu">
          <div className="share-menu-overlay" onClick={handleShareJob}></div>
          <div className="share-menu-content">
            <h3>Chia sẻ việc làm</h3>
            <div className="share-options">
              <button onClick={() => shareVia('copy')}>Sao chép link</button>
              <button onClick={() => shareVia('facebook')}>Facebook</button>
              <button onClick={() => shareVia('linkedin')}>LinkedIn</button>
              <button onClick={() => shareVia('email')}>Email</button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Section */}
      <SubscriptionSection />
    </div>
  );
};

export default JobDetail; 